import { ComponentType, lazy, LazyExoticComponent } from "react";

/**
 * Route-level code splitting with a "reload once" safety net.
 *
 * Every deploy publishes the frontend to a fresh GCS bucket with new, hashed chunk
 * names; the old chunks are no longer served (the bucket's error page is index.html,
 * so a stale chunk URL even returns HTML). A tab that was opened before the deploy
 * and navigates to a not-yet-loaded route would get
 * "Failed to fetch dynamically imported module" and a blank page.
 *
 * `lazyWithRetry` catches that failure and reloads the page once — but ONLY when
 * index.html on the server has actually changed (a new deploy). A chunk that fails
 * for any other reason (network hiccup, a blocked or rate-limited request, ...) is
 * surfaced to the ErrorBoundary instead: reloading would not help and, as seen in
 * production on 2026-09-03, turns a single failed request into a reload storm and a
 * blank page.
 *
 * Prefetches (`.preload()`) never trigger a reload: a failed warm-up is simply
 * dropped, the real load at navigation time gets its own attempt.
 *
 * Lessons from the preview test:
 * - Never call `preventDefault()` on Vite's `vite:preloadError`: Vite then swallows
 *   the error and resolves the `import()` with `undefined`, which looks like success.
 * - The reload flag must only be cleared by a successful load, never by a failure.
 */

const RELOAD_FLAG = "dashly:chunk-reload";
/**
 * A reload counts as "recent" for this long. Within the window a second failure is
 * surfaced instead of reloading again (that is what stops a reload loop); after it a
 * later stale chunk in the same tab may trigger a fresh reload.
 */
export const RELOAD_FLAG_WINDOW_MS = 30_000;

/** Set as soon as this page instance has asked the browser to reload. */
let reloadInProgress = false;

const readReloadFlag = (): boolean => {
	try {
		const raw = window.sessionStorage.getItem(RELOAD_FLAG);
		if (raw === null) {
			return false;
		}
		const reloadedAt = Number(raw);
		return Number.isFinite(reloadedAt) && Date.now() - reloadedAt < RELOAD_FLAG_WINDOW_MS;
	} catch {
		return false;
	}
};

const writeReloadFlag = (): void => {
	try {
		window.sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
	} catch {
		// sessionStorage may be unavailable; we then simply never reload automatically
	}
};

export const clearReloadFlag = (): void => {
	try {
		window.sessionStorage.removeItem(RELOAD_FLAG);
	} catch {
		// ignore
	}
};

/**
 * The entry script this page was booted with, e.g. "index-Fl6mFSNu.js". Captured
 * once at startup; a freshly fetched index.html that no longer references it means
 * a new version has been deployed.
 */
let currentEntryScript: string | null | undefined;

const entryScriptName = (): string | null => {
	if (currentEntryScript === undefined) {
		const script = document.querySelector<HTMLScriptElement>('script[type="module"][src]');
		const src = script?.getAttribute("src") ?? null;
		currentEntryScript = src ? src.split("/").pop() ?? null : null;
	}
	return currentEntryScript;
};

/**
 * Asks the server for the current index.html (bypassing caches) and reports whether
 * it still references this page's entry script. `null` when it cannot be determined
 * (fetch failed) — callers treat that as "do not reload".
 */
export const isNewVersionDeployed = async (): Promise<boolean | null> => {
	const entry = entryScriptName();
	if (!entry) {
		return null;
	}
	try {
		const url = new URL("index.html", document.baseURI);
		url.searchParams.set("_v", String(Date.now()));
		const response = await fetch(url.toString(), { cache: "no-store", credentials: "same-origin" });
		if (!response.ok) {
			return null;
		}
		const html = await response.text();
		return !html.includes(entry);
	} catch {
		return null;
	}
};

/**
 * Reloads the page once in reaction to a chunk-load failure during a real
 * navigation, but only when a new version is actually deployed.
 * Resolves `true` when a reload is (already) under way, `false` when the caller
 * should surface the error instead.
 */
export const reloadOnceForStaleChunk = async (): Promise<boolean> => {
	if (reloadInProgress) {
		return true;
	}
	if (readReloadFlag()) {
		// Deliberately not cleared: a successful chunk load clears it, otherwise it expires.
		return false;
	}
	if (await isNewVersionDeployed() !== true) {
		// Same version on the server (or unknown): the chunk is not stale, a reload
		// would not fix anything. Surface the error.
		return false;
	}
	writeReloadFlag();
	reloadInProgress = true;
	window.location.reload();
	return true;
};

const isModuleWithDefault = <T,>(value: unknown): value is { default: T } =>
	typeof value === "object" && value !== null && "default" in value;

/** A lazy route component that can also be warmed up ahead of navigation. */
export type LazyRouteComponent<T extends ComponentType<any>> = LazyExoticComponent<T> & {
	/** Fetches the chunk without rendering. Rejects on failure (callers decide what to do); never reloads. */
	preload: () => Promise<void>;
};

export function lazyWithRetry<T extends ComponentType<any>>(
	factory: () => Promise<{ default: T }>,
): LazyRouteComponent<T> {
	let inFlight: Promise<{ default: T }> | null = null;

	const load = (): Promise<{ default: T }> => {
		if (!inFlight) {
			inFlight = factory()
				.then((loaded) => {
					if (!isModuleWithDefault<T>(loaded)) {
						// A swallowed load error resolves to undefined; treat it as a failure.
						throw new Error("Route chunk resolved without a module");
					}
					return loaded;
				})
				.catch((error: unknown) => {
					inFlight = null; // allow a later attempt to try again
					throw error;
				});
		}
		return inFlight;
	};

	const component = lazy(async () => {
		try {
			const loaded = await load();
			clearReloadFlag();
			return loaded;
		} catch (error) {
			if (await reloadOnceForStaleChunk()) {
				// Keep Suspense pending until the reload takes over.
				return new Promise<{ default: T }>(() => undefined);
			}
			throw error;
		}
	}) as LazyRouteComponent<T>;

	component.preload = () => load().then(() => undefined);
	return component;
}

/**
 * Registers a window-level handler for Vite's preload failures (a CSS or shared JS
 * chunk that fails before the route chunk itself). It only logs: Vite still rejects
 * the `import()` (the event is left un-prevented on purpose), so the lazy path above
 * decides — with the version check — whether a reload is warranted. Reloading from
 * here directly would also fire for failed *prefetches*, which must never reload.
 */
export const installChunkErrorReload = (): void => {
	window.addEventListener("vite:preloadError", (event) => {
		const payload = (event as Event & { payload?: unknown }).payload;
		console.warn("[dashly] chunk preload failed", payload);
	});
};
